import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  Navigate,
} from "react-router-dom";
import moment from "moment";
import { useQuery } from "react-query";
import useApi from "../../hooks/useApi";
import useApp from "../../hooks/useApp";
import useReload from "../../hooks/useReload";
import { Accordion, Button, Nav, Card, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FilterBar, ListItemWidget } from "../../widgets";
import PolicyItemStatus from "../../widgets/PolicyItemStatus";
import ClientClaimRequirement from "../policies/ClientClaimRequirement";
import SignaturePad from "signature_pad";

const ClaimForm = (props) => {
  const { scope = "add" } = props;
  const { clientPolicyId } = useParams();
  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();
  const { reload } = useReload();
  const [wizardStep, setWizardStep] = useState(1);
  const [activeWizard, setActiveWizard] = useState();
  const [startDate, setStartDate] = useState(new Date());
  const navigate = useNavigate();
  const [claim, setClaim] = useState({
    ClientPolicyId: clientPolicyId,
    incidentDate: startDate,
    reportDate: startDate,
    incidentDescription: "",
    claimRequirement: "",
    declaration: false,
  });

  const getPolicy = async () => {
    try {
      if (clientPolicyId) {
        const { data = {} } = await api.read(
          `/client-policies/${clientPolicyId}`
        );
        return data;
      }
    } catch (error) {
      throw new Error("failed to fetch records");
    }
  };

  const { data: policy } = useQuery(
    ["policy", clientPolicyId, auth],
    ({ queryKey }) => getPolicy()
  );

  useEffect(() => {
    if (wizardStep === 3) {
      // const canvas = document.querySelector("canvas");
      // const signaturePad = new SignaturePad(canvas);
    }

    setActiveWizard(`link-${wizardStep}`);
  }, [wizardStep]);

  const submitClaim = async () => {
    const response = await api.add("/claims", claim);
    if (response.ok) {
      const { data } = response;
      // console.log(data);
      showAlert("Claim submitted", "success");

      navigate(`/claims/${data.id}`, { replace: true });
    }
  };

  return (
    <section id="content" className="content">
      <div className="content__header content__boxed">
        <div className="content__wrap">
          {/* <!-- Breadcrumb --> */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/desktop">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/claims">Claims</Link>
              </li>
              <li className="breadcrumb-item" aria-current="page">
                <Link to="/claims">
                  {policy?.Client?.name || clientPolicyId}
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Log Claim
              </li>
            </ol>
          </nav>
          {/* <!-- END : Breadcrumb --> */}

          <h1 className="page-title mb-0 mt-2">
            {policy?.Client?.name || "Policy detail"}
          </h1>

          <p className="lead">Register Claim</p>
          <div className="d-md-flex align-items-baseline my-3">
            <FilterBar />
          </div>
        </div>
      </div>

      <div className="content__boxed">
        <div className="content__wrap">
          <div className="row">
            <div className="col-xl-7 col-lg-7 col-sm-12">
              <Card>
                <Card.Header>
                  <Nav
                    variant="pills"
                    activeKey={activeWizard}
                    defaultActiveKey="link-1"
                    className="gap-2"
                    onSelect={(nav) => {
                      setActiveWizard(nav);
                      setWizardStep(
                        nav === "link-1"
                          ? 1
                          : nav === "link-2"
                          ? 2
                          : nav === "link-3"
                          ? 3
                          : 4
                      );
                    }}
                  >
                    <Nav.Item>
                      <Nav.Link eventKey="link-1">
                        <i className="bi bi-info-circle-fill me-2"></i>
                        Information
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="link-2"
                        disabled={
                          !scope === "add" || wizardStep > 1 ? false : true
                        }
                      >
                        <i className="bi bi-file-binary-fill me-2"></i>
                        Requirements
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="link-3"
                        disabled={
                          !scope === "add" || wizardStep > 2 ? false : true
                        }
                      >
                        <i className="bi bi-postcard-fill me-2"></i>
                        Declaration
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="link-4"
                        disabled={
                          !scope === "add" || wizardStep > 3 ? false : true
                        }
                      >
                        <i className="bi bi-door-closed-fill me-2"></i>
                        Submit
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>
                <Card.Body>
                  {wizardStep === 1 && (
                    <div>
                      <Form>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-primary">
                            Date of Incident/Accident
                          </Form.Label>
                          <DatePicker
                            dateFormat="dd/MM/yyyy"
                            showMonthDropdown
                            showPopperArrow={false}
                            id="effective-date"
                            className="form-control"
                            value={claim?.incidentDate || startDate}
                            selected={claim?.incidentDate || startDate}
                            onChange={(date) =>
                              setClaim({ ...claim, incidentDate: date })
                            }
                            isClearable
                            placeholderText=""
                          />
                          <Form.Text></Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-primary">
                            Description of Incident/Accident
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            required
                            rows={8}
                            value={claim?.incidentDescription}
                            onChange={(e) =>
                              setClaim({
                                ...claim,
                                incidentDescription: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-primary">
                            Date Incident/Accident Reported
                          </Form.Label>
                          <DatePicker
                            dateFormat="dd/MM/yyyy"
                            showMonthDropdown
                            showPopperArrow={false}
                            id="effective-date"
                            className="form-control"
                            value={claim?.reportDate || startDate}
                            selected={claim?.reportDate || startDate}
                            onChange={(date) =>
                              setClaim({ ...claim, reportDate: date })
                            }
                            isClearable
                            placeholderText=""
                          />
                          <Form.Text></Form.Text>
                        </Form.Group>

                        {/* <Button
                          variant="primary"
                          onClick={() => saveClaimInformation()}
                        >
                          <i className="bi bi-save-fill me-2 fs-5"></i>
                          Next
                        </Button> */}
                      </Form>
                    </div>
                  )}
                  {wizardStep === 2 && (
                    <>
                      <ClientClaimRequirement policy={policy} />
                      {/* <Button
                        variant="primary"
                        onClick={() => saveClaimInformation()}
                      >
                        <i className="bi bi-save-fill me-2 fs-5"></i>
                        Next
                      </Button> */}
                    </>
                  )}
                  {wizardStep === 3 && (
                    <div>
                      <Form.Check
                        type="switch"
                        id="declaration"
                        checked={claim?.declaration == true ? true : false}
                        onChange={(e) =>
                          setClaim({ ...claim, declaration: e.target.checked })
                        }
                        label="I/We declare that the above answers are true and complete."
                      />
                      {/* <div className="border mt-3">
                        <canvas
                          id="signature-pad"
                          className="w-100"
                          style={{ height: 200 }}
                        ></canvas>
                      </div> */}

                      {/* <Button
                        variant="primary"
                        onClick={() => saveClaimInformation()}
                      >
                        <i className="bi bi-save-fill me-2 fs-5"></i>
                        Next
                      </Button> */}
                    </div>
                  )}
                  {wizardStep === 4 && (
                    <div>
                      <p>Summary</p>
                    </div>
                  )}
                </Card.Body>
                {scope === "add" && (
                  <Card.Footer>
                    <div
                      className={
                        wizardStep == 1
                          ? "d-flex justify-content-end"
                          : "d-flex  justify-content-between"
                      }
                    >
                      {wizardStep > 1 && (
                        <Button
                          variant="info"
                          onClick={() => {
                            const step = wizardStep - 1;
                            setActiveWizard(`link-${step}`);
                            setWizardStep(step);
                          }}
                        >
                          Previous
                        </Button>
                      )}
                      {wizardStep < 4 && (
                        <Button
                          variant="primary"
                          onClick={() => {
                            const step = wizardStep + 1;
                            setActiveWizard(`link-${step}`);
                            setWizardStep(step);
                          }}
                        >
                          Next
                        </Button>
                      )}
                      {wizardStep == 4 && (
                        <Button variant="success" onClick={() => submitClaim()}>
                          Submit
                        </Button>
                      )}
                    </div>
                  </Card.Footer>
                )}
              </Card>
              {/* <Accordion defaultActiveKey="0" className="mb-2">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Claim Requirements</Accordion.Header>
                  <Accordion.Body></Accordion.Body>
                </Accordion.Item>
              </Accordion> */}
            </div>
            <div className="col-xl-5 col-lg-5 col-sm-12">
              <div className="card mb-3">
                <div className="card-header">
                  <PolicyItemStatus status={policy?.status} />
                </div>
                <div className="card-body">
                  <ListItemWidget title={"Title"} text={policy?.title} />
                  <ListItemWidget
                    title={"Product"}
                    text={
                      policy?.PolicyType?.name + " - " + policy?.Product?.name
                    }
                  />
                  <ListItemWidget
                    title={"Insuring Company"}
                    text={policy?.Company?.name + " - " + policy?.Branch?.name}
                  />
                  <ListItemWidget
                    title={"Policy Number"}
                    text={policy?.number}
                  />
                  <ListItemWidget
                    title={"Expires"}
                    text={moment(policy?.expires).format("DD/MM/YYYY")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClaimForm;
